import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import useLenis, { scrollToTop, scrollToY } from "@/hooks/useLenis";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import About from "@/pages/About";
import FAQ from "@/pages/FAQ";
import Account from "@/pages/Account";
import AuthCallback from "@/pages/AuthCallback";
import Track from "@/pages/Track";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

// Remembers scroll position per route so Back/Forward returns the shopper to
// exactly where they were in the catalog; fresh navigations still start at top.
const scrollMemory = new Map();

const ScrollToTop = () => {
  const location = useLocation();
  const navType = useNavigationType();
  const key = location.pathname + location.search;
  useEffect(() => {
    const onScroll = () => scrollMemory.set(key, window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [key]);
  useEffect(() => {
    if (navType === "POP" && scrollMemory.has(key)) {
      // Grid content reloads async after back-nav — keep retrying until the page
      // is tall enough to actually hold the remembered position.
      const y = scrollMemory.get(key);
      const iv = setInterval(() => {
        scrollToY(y);
        if (Math.abs(window.scrollY - y) < 60) clearInterval(iv);
      }, 250);
      const stop = setTimeout(() => clearInterval(iv), 4000);
      return () => { clearInterval(iv); clearTimeout(stop); };
    }
    scrollToTop();
  }, [key, navType]);
  return null;
};

// Captures ?ref=CODE referral links (e.g. fleecetheregime.com/shop?ref=LAURY1A2B)
// so the checkout page can prefill the referral code.
const ReferralCapture = () => {
  const location = useLocation();
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get("ref");
    if (ref) localStorage.setItem("ftr_ref", ref.trim().toUpperCase());
  }, [location.search]);
  return null;
};

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/shop" element={<Shop />} />
      <Route path="/product/:slug" element={<ProductDetail />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/about" element={<About />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/account" element={<Account />} />
      <Route path="/track" element={<Track />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  useLenis();
  return (
    <CartProvider>
      <BrowserRouter>
        <ScrollToTop />
        <ReferralCapture />
        <div className="grain-overlay" aria-hidden />
        <Navbar />
        <CartDrawer />
        <AppRoutes />
        <Footer />
        <Toaster
          position="bottom-center"
          toastOptions={{
            style: {
              background: "#0A0A0A",
              color: "#F4F4F0",
              border: "2px solid #0A0A0A",
              borderRadius: 0,
              fontFamily: "'IBM Plex Mono', monospace",
            },
          }}
        />
      </BrowserRouter>
    </CartProvider>
  );
}

export default App;
